import type { ButtonHTMLAttributes, FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'light' | 'outline-light'
type ButtonSize = 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant
    size?: ButtonSize
}

const base = 'inline-flex shrink-0 items-center justify-center gap-2 rounded-full font-semibold whitespace-nowrap transition-[background-color,color,border-color,box-shadow,transform] duration-200 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:translate-y-px disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0'

const variants: Record<ButtonVariant, string> = {
    'primary': 'bg-primary text-primary-foreground shadow-sm shadow-primary/25 hover:bg-primary/90 hover:shadow-md hover:shadow-primary/25',
    'secondary': 'bg-secondary text-secondary-foreground hover:bg-accent',
    'outline': 'border border-foreground/15 bg-transparent text-foreground hover:border-foreground/30 hover:bg-foreground/5',
    'ghost': 'text-foreground hover:bg-foreground/5',
    'light': 'bg-broth-foreground text-broth hover:bg-white',
    'outline-light': 'border border-broth-foreground/25 text-broth-foreground hover:border-broth-foreground/50 hover:bg-broth-foreground/10'
}

const sizes: Record<ButtonSize, string> = {
    'sm': 'h-9 px-4 text-sm [&_svg]:size-4',
    'md': 'h-11 px-5 text-sm [&_svg]:size-4',
    'lg': 'h-12 px-6 text-base [&_svg]:size-5',
    'icon': 'size-11 [&_svg]:size-5',
    'icon-sm': 'size-9 [&_svg]:size-4'
}

export const buttonClasses = (variant: ButtonVariant = 'primary', size: ButtonSize = 'md', className?: string): string => cn(base, variants[variant], sizes[size], className)

export const Button: FC<ButtonProps> = ({
    variant = 'primary',
    size = 'md',
    className,
    type = 'button',
    ...props
}): ReactNode => {
    return (
        <button
            type={type}
            className={buttonClasses(variant, size, className)}
            {...props}
        />
    )
}
